/**
 * Global utility functions for Ishinomaki theme
 * Based on Dawn theme architecture
 */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Defer initialization to ensure children are parsed
    requestAnimationFrame(() => {
      this.init();
    });
  }
  
  init() {
    this.input = this.querySelector('input');
    
    // Guard against null input
    if (!this.input) return;
    
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    
    const buttons = this.querySelectorAll('button');
    
    buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
    
    this.validateQtyRules();
    
    // Subscribe only if PUB_SUB_EVENTS is available
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.input) return;
    
    // Find the actual button (might click on span inside button)
    const button = event.target.closest('button');
    if (!button) return;
    
    const currentValue = parseInt(this.input.value) || 1;
    const min = parseInt(this.input.min) || 1;
    const max = this.input.max ? parseInt(this.input.max) : Infinity;

    if (button.name === 'plus' && currentValue < max) {
      this.input.value = currentValue + 1;
      this.input.dispatchEvent(this.changeEvent);
    } else if (button.name === 'minus' && currentValue > min) {
      this.input.value = currentValue - 1;
      this.input.dispatchEvent(this.changeEvent);
    }
    
    this.validateQtyRules();
  }

  validateQtyRules() {
    if (!this.input) return;
    
    const value = parseInt(this.input.value);
    const min = parseInt(this.input.min) || 1;
    
    const buttonMinus = this.querySelector("[name='minus']");
    if (buttonMinus) {
      buttonMinus.disabled = value <= min;
    }
    
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector("[name='plus']");
      if (buttonPlus) {
        buttonPlus.disabled = value >= max;
      }
    }
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}
